import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';

import { GenericModalService } from '../shared/generic-modal/generic-modal.service';
import { AuthenticationService } from '../shared/authentication/authentication.service';
import { LoginObserverService } from '../shared/authentication/login-observer.service';
import { SavedTrackingSearchService, SavedTrackingSearch } from '../tracking/services/saved-searches.service';
import { MENU, MenuItem, MenuChildItem } from './menu-definition';
import { DateTimeService } from '../shared/datetime/datetime.service';
import { PartnerFavoritesService, SavedPartnerFavorite } from '../partner/services/partner-favorites.service';
import { DeveloperService } from '../developer/services/developer.service';
import { TrackingSharedActionMethodsService } from '../tracking/services/tracking-shared-action-methods.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';

import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';

@Component({
    selector: 'ediportal-mainmenu',
    templateUrl: './mainmenu.component.html',
    styleUrls: ['./mainmenu.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterLink, PermissionRequiredDirective, RouterLinkActive, SvgIconComponent]
})
export class MainMenuComponent implements OnInit {

    constructor(
        private loginService: LoginObserverService,
        private authenticationService: AuthenticationService,
        private genericModalService: GenericModalService,
        private savedTrackingSearchesService: SavedTrackingSearchService,
        public dateTimeService: DateTimeService,
        private router: Router,
        private partnerFavoritesService: PartnerFavoritesService,
        private developerService: DeveloperService,
        private trackingService: TrackingSharedActionMethodsService,
        private toastService: ToastService) { }


    public isUserLoggedIn: boolean;
    private savedTrackingSearches: SavedTrackingSearch[] = [];
    private partnerFavorites: SavedPartnerFavorite[] = [];
    private menuItems: MenuItem[];
    private openMenu: string;
    private trackingMenu: MenuItem;
    private partnerMenu: MenuItem;

    ngOnInit(): void {
        this.menuItems = MENU;

        // Find tracking search menu group
        this.trackingMenu = this.menuItems.find((e: MenuItem) => e.name == 'tracking');
        this.partnerMenu = this.menuItems.find((e: MenuItem) => e.name == 'partners');

        this.authenticationService.isLoggedIn().then((loggedIn: boolean) => {
            this.isUserLoggedIn = loggedIn;
        });

        // Listen for changes in the user-login
        this.loginService.loginObserver.subscribe(() => {
            this.isUserLoggedIn = true;
        });
        this.loginService.logoutObserver.subscribe(() => {
            this.isUserLoggedIn = false;
        });

        this.loadTrackingSearches();
        this.loadPartnerFavorites();

        this.partnerFavoritesService.saveSearchObserver.subscribe((partnerFavorite) => {
            this.partnerFavorites.push(partnerFavorite);
            this.updatePartnerFavoritesMenu();
        });

        this.savedTrackingSearchesService.saveSearchObserver.subscribe((search) => {
            this.savedTrackingSearches.push(search);
            this.savedTrackingSearches.sort((s1, s2) => s1.name.localeCompare(s2.name));
            this.updateSavedTrackingSearchMenu();
        });
    }


    showMenu(menu: string): void {
        let container: HTMLElement = this.getMenuContainer(menu);
        if (container != null) {
            container.className = 'sublevel';
            container.style.display = '';
        }
        this.openMenu = menu;
    }

    hideMenu(): void {
        if (this.openMenu) {
            let container: HTMLElement = this.getMenuContainer(this.openMenu);
            if (container != null) {
                container.className = '';
                container.style.display = 'none';
            }
            this.openMenu = '';
        }
    }

    clickMenuItem(event: any, menuItem: MenuItem) {
        const alreadyOpenMenu: string = this.openMenu;
        this.hideMenu();

        // When the large L is clicked, there is no menu item and the event should not be processed any further;
        // this is only necessary to close the menu in case it's open.

        if (menuItem) {
            // Stop the event propagation because we'll take over the navigation from here.
            event.stopPropagation();

            // If the menu item name is the same as the one, which was open until closed above, then don't do anymore.
            if (menuItem.name != alreadyOpenMenu) {

                // If there are no child groups navigate directly to the main items link. Otherwise, check if there is
                // exactly one group with one child item in it and in that case navigate directly to that items link. In
                // remaining cases, just open the relevant sub menu.
                if (!menuItem.groups || menuItem.groups.length == 0) {
                    this.navigateTo(menuItem.link);
                }
                else if (menuItem.groups.length == 1 && menuItem.groups[0].items && menuItem.groups[0].items.length == 1) {
                    this.navigateTo(menuItem.groups[0].items[0].link);
                } else {
                    this.showMenu(menuItem.name);
                }
            }
        }
    }

    doubleClickMenuItem(event: any, menuItem: MenuItem): void {
        this.hideMenu();
        this.navigateTo(menuItem.link);
    }

    clickMenuChildItem(event: any, childItem: MenuChildItem) {
        this.hideMenu();

        // Stop the event propagation because it'll cause an extra navigation to an incorrect route.
        event.stopPropagation();

        // If the child item's param has a force value, then increment it. The purpose of this is to
        // ensure that every click will generate a different url than the last.
        if (childItem && childItem.param && childItem.param.force) {
            ++childItem.param.force;
        }

        // Check for some special tags that must be handled differently.
        if (childItem && childItem.tag) {
            if (childItem.tag.clearCache) {
                this.clearCache();
            }
            if (childItem.tag.uploadDocument == true) {
                this.uploadDocument();
            }
        }
    }

    async uploadDocument() {
        await this.trackingService.uploadDocument('Configure the uploaded document');
    }

    async clearCache() {
        this.genericModalService.confirmDirect('Clear cache', 'This will clear the local cache and broadcast a clear-cache events to all running hosts. Continue?').then(async () => {
            await this.developerService.clearCache();
            this.toastService.showToast('The cache has now been cleared.', ToastType.info);
        }).catch(() => { });
    }

    navigateTo(link: string): void {
        if (link) {
            this.router.navigate(['/' + link]);
        }
    }

    getMenuContainer(menu: string): HTMLElement | null {
        return document.getElementById('ul' + menu);
    }

    loadTrackingSearches(): void {
        this.savedTrackingSearchesService.getSearches().then((searches: SavedTrackingSearch[]) => {
            this.savedTrackingSearches = searches;
            this.updateSavedTrackingSearchMenu();
        });
    }

    loadPartnerFavorites(): void {
        this.partnerFavoritesService.getSearches().then((favorites: SavedPartnerFavorite[]) => {
            this.partnerFavorites = favorites;
            this.updatePartnerFavoritesMenu();
        });
    }

    updatePartnerFavoritesMenu(): void {
        if (this.partnerMenu) {
            // Remove existing group; if any
            this.partnerMenu.groups = this.partnerMenu.groups.filter(e => !e.tag || !e.tag.isPartnerFavorites);

            if (this.partnerFavorites.length > 0) {
                this.partnerMenu.groups.push({
                    caption: 'Favorites',
                    tag: { "isPartnerFavorites": true },
                    items: this.partnerFavorites.map(e => {
                        return {
                            permission: 'Partner_View_PartnerDetails',
                            name: e.name,
                            link: 'partner/details',
                            param: e.params,
                            tag: { "savedPartnerFavorite": e }
                        };
                    })
                });
            }
        }
    }

    removePartnerFavorite(savedPartnerFavorite: SavedPartnerFavorite) {
        if (savedPartnerFavorite) {
            this.genericModalService.confirmDirect('Delete favorite', `Delete ${savedPartnerFavorite.name}?`).then(() => {
                this.partnerFavoritesService.deleteSearch(savedPartnerFavorite).then(() => {
                    this.partnerFavorites.splice(this.partnerFavorites.indexOf(savedPartnerFavorite), 1);
                    this.updatePartnerFavoritesMenu();
                });
            }).catch(() => { });
        }
    }

    updateSavedTrackingSearchMenu(): void {
        if (this.trackingMenu) {
            // Remove existing group; if any
            this.trackingMenu.groups = this.trackingMenu.groups.filter(e => !e.tag || !e.tag.isSavedTrackingSearchGroup);

            if (this.savedTrackingSearches.length > 0) {
                this.trackingMenu.groups.push({
                    caption: 'Saved Searches',
                    tag: { "isSavedTrackingSearchGroup": true },
                    items: this.savedTrackingSearches.map(e => {
                        const newParams = { ...e.params, isSavedSearch: true };
                        return {
                            permission: 'Tracking_View_SearchResult',
                            name: e.name,
                            link: 'tracking/result',
                            param: newParams,
                            tag: { "savedTrackingSearch": e }
                        };
                    })
                });
            }
        }
    }

    removeTrackingSearch(savedTrackingSearch: SavedTrackingSearch) {
        if (savedTrackingSearch) {
            this.genericModalService.confirmDirect('Delete search', `Delete ${savedTrackingSearch.name}?`).then(() => {
                this.savedTrackingSearchesService.deleteSearch(savedTrackingSearch).then(() => {
                    this.savedTrackingSearches.splice(this.savedTrackingSearches.indexOf(savedTrackingSearch), 1);
                    this.updateSavedTrackingSearchMenu();
                });
            }).catch(() => { });
        }
    }
}
